import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { DateInputFR } from '../../DateInputFR';
import { STATUTS_ACTION, ORIGINES_ACTION } from '../constantes';

const VIDE = { origine: 'autre', origine_ref: '', origine_libelle: '', constat: '', action: '', responsable: '', echeance: '', statut: 'a_faire', resultat: '', indicateurs: [32] };

const Champ = ({ label, children }) => <label className="block space-y-1 text-sm"><span className="font-medium text-slate-700">{label}</span>{children}</label>;

const select = 'h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm';

const ChoixIndicateurs = ({ valeur, onChange }) => (
  <div className="flex flex-wrap gap-1" data-testid="action-indicateurs">
    {Array.from({ length: 32 }, (_, i) => i + 1).map((n) => {
      const actif = valeur.includes(n);
      return (
        <button key={n} type="button" onClick={() => onChange(actif ? valeur.filter((x) => x !== n) : [...valeur, n].sort((a, b) => a - b))}
          className={`h-7 w-7 rounded-md border text-xs font-semibold transition-colors ${actif ? 'border-[#8DC63F] bg-[#8DC63F] text-[#0F172A]' : 'border-slate-200 text-slate-500 hover:border-slate-400'}`}
          data-testid={`action-indicateur-${n}`}>{n}</button>
      );
    })}
  </div>
);

// Création / modification d'une action ; `action` = action existante ou pré-remplissage (suggestion, réclamation)
export const ActionDialog = ({ action, onClose, onSaved }) => {
  const [f, setF] = useState(VIDE);
  const [busy, setBusy] = useState(false);
  const [confirmer, setConfirmer] = useState(false);
  useEffect(() => { setF({ ...VIDE, ...(action || {}) }); setConfirmer(false); }, [action]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const existante = !!action?.action_id;
  const enregistrer = async () => {
    setBusy(true);
    try {
      const champs = { ...f, indicateurs: f.indicateurs || [] };
      const { data } = existante ? await axios.put(`/qualiopi/actions/${action.action_id}`, champs) : await axios.post('/qualiopi/actions', champs);
      toast.success(existante ? 'Action mise à jour' : `Action ${data.numero} ajoutée au plan d'amélioration`);
      onSaved(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'enregistrement"); } finally { setBusy(false); }
  };
  const supprimer = async () => {
    try { await axios.delete(`/qualiopi/actions/${action.action_id}`); toast.success('Action supprimée'); onSaved(null); } catch (e) { toast.error(e.response?.data?.detail || 'Suppression impossible'); }
  };
  return (
    <Dialog open={!!action} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="action-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{existante ? `Action ${action.numero}` : "Nouvelle action d'amélioration"}</DialogTitle>
          <DialogDescription>Constat → action → responsable → échéance → résultat : la trace attendue pour l'indicateur 32.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <Champ label="Origine"><select value={f.origine} onChange={set('origine')} className={select} data-testid="action-origine">{Object.entries(ORIGINES_ACTION).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Champ>
          <Champ label="Source (référence)"><Input value={f.origine_libelle} onChange={set('origine_libelle')} placeholder="Ex : questionnaire diplômés 2026" data-testid="action-source" /></Champ>
          <div className="sm:col-span-2"><Champ label="Constat *"><Textarea rows={2} value={f.constat} onChange={set('constat')} data-testid="action-constat" /></Champ></div>
          <div className="sm:col-span-2"><Champ label="Action décidée *"><Textarea rows={2} value={f.action} onChange={set('action')} data-testid="action-action" /></Champ></div>
          <Champ label="Responsable"><Input value={f.responsable} onChange={set('responsable')} data-testid="action-responsable" /></Champ>
          <Champ label="Échéance"><DateInputFR value={f.echeance} onChange={set('echeance')} data-testid="action-echeance" /></Champ>
          <Champ label="Statut"><select value={f.statut} onChange={set('statut')} className={select} data-testid="action-statut">{Object.entries(STATUTS_ACTION).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></Champ>
          <div className="sm:col-span-2"><Champ label="Résultat / mesure de l'efficacité"><Textarea rows={2} value={f.resultat} onChange={set('resultat')} placeholder="Ce qui a changé, comment vous l'avez vérifié" data-testid="action-resultat" /></Champ></div>
          <div className="sm:col-span-2"><Champ label="Indicateurs Qualiopi concernés"><ChoixIndicateurs valeur={f.indicateurs || []} onChange={set('indicateurs')} /></Champ></div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
          {existante ? (confirmer
            ? <span className="flex gap-1"><Button variant="destructive" size="sm" onClick={supprimer} data-testid="action-supprimer-confirm">Confirmer la suppression</Button><Button variant="ghost" size="sm" onClick={() => setConfirmer(false)}>Annuler</Button></span>
            : <Button variant="ghost" size="sm" className="text-red-600" onClick={() => setConfirmer(true)} data-testid="action-supprimer">Supprimer</Button>) : <span />}
          <Button onClick={enregistrer} disabled={busy || !f.constat.trim() || !f.action.trim()} className="bg-[#0F172A] text-white hover:bg-[#1e293b]" data-testid="action-enregistrer">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrer'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
